import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ShoppingCart, Heart, Star, Minus, Plus } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { productsApi } from '@/services/api';
//import { Product } from '@/services/types/domain';
import { useCart } from '@/store/cartStore';
import { useAuth } from '@/store/authStore';
import Button from '@/components/ui/Button';
import Image from '@/components/ui/Image';
import { formatCurrency, calculateDiscountPercentage, UPLOADS_URL } from '@/utils/utils';
import { toast } from 'react-hot-toast';
import { cn } from '@/utils/utils';

const ProductDetailPage: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const navigate = useNavigate();
    const { addProduct } = useCart();
    const { isAuthenticated } = useAuth();
    const [selectedImage, setSelectedImage] = useState(0);
    const [selectedVariant, setSelectedVariant] = useState<string | null>(null);
    const [quantity, setQuantity] = useState(1);
    const [isWishlist, setIsWishlist] = useState(false);

    // Fetch product data
    const { data: product, isLoading, isError } = useQuery({
        queryKey: ['product', slug],
        queryFn: () => productsApi.getProductBySlug(slug || ''),
        enabled: !!slug,
    });

    // Handle add to cart
    const handleAddToCart = async () => {
        if (!isAuthenticated) {
            toast.error('Debes iniciar sesión para agregar productos al carrito');
            return;
        }

        try {
            await addProduct(product?.id || 0, quantity, selectedVariant ? parseInt(selectedVariant) : undefined);
            toast.success('Producto agregado al carrito');
        } catch (error) {
            toast.error('Error al agregar al carrito');
        }
    };

    // Handle wishlist toggle
    const handleWishlistToggle = () => {
        if (!isAuthenticated) {
            toast.error('Debes iniciar sesión para guardar favoritos');
            return;
        }
        setIsWishlist(!isWishlist);
        toast.success(!isWishlist ? 'Agregado a favoritos' : 'Removido de favoritos');
    };

    if (isLoading) {
        return (
            <div className="container mx-auto px-4 py-8 flex justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
            </div>
        );
    }

    if (isError || !product) {
        return (
            <div className="container mx-auto px-4 py-8 text-center">
                <div className="bg-red-50 text-red-600 p-4 rounded-lg inline-block">
                    Error al cargar el producto. Intenta nuevamente.
                </div>
                <Button
                    onClick={() => navigate(-1)}
                    variant="outline"
                    className="mt-4"
                    leftIcon={<ArrowLeft className="w-5 h-5" />}
                >
                    Volver atrás
                </Button>
            </div>
        );
    }

    // Calculate discount if exists
    const hasDiscount = product.compare_price && product.compare_price > product.price;
    const discountPercentage = hasDiscount
        ? calculateDiscountPercentage(product.compare_price || 0, product.price)
        : 0;

    return (
        <div className="bg-gray-50 min-h-screen">
            <div className="container mx-auto px-4 py-8">
                {/* Breadcrumb and back button */}
                <div className="flex items-center gap-4 mb-6">
                    <Button
                        onClick={() => navigate(-1)}
                        variant="ghost"
                        size="sm"
                        leftIcon={<ArrowLeft className="w-5 h-5" />}
                    >
                        Volver
                    </Button>
                    <div className="text-sm text-gray-500">
                        {product.category?.name || 'Producto'} / {product.name}
                    </div>
                </div>

                {/* Product main section */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-6">
                        {/* Product images */}
                        <div className="space-y-4">
                            {/* Main image */}
                            <div className="bg-gray-100 rounded-lg overflow-hidden aspect-square">
                                <Image
                                    src={UPLOADS_URL + (product.images?.[selectedImage]?.image_url || '')}
                                    alt={product.images?.[selectedImage]?.alt_text || product.name}
                                    //fallback={placeholders.product}
                                    className="w-full h-full object-contain"
                                />
                            </div>

                            {/* Thumbnails */}
                            {product.images && product.images.length > 1 && (
                                <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
                                    {product.images.map((image, index) => (
                                        <button
                                            key={index}
                                            onClick={() => setSelectedImage(index)}
                                            className={cn(
                                                'border-2 rounded-md overflow-hidden aspect-square',
                                                selectedImage === index ? 'border-primary-500' : 'border-transparent'
                                            )}
                                        >
                                            <Image
                                                src={UPLOADS_URL + image.image_url}
                                                alt={image.alt_text || product.name}
                                                className="w-full h-full object-cover"
                                                showPlaceholder={false}
                                            />
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Product details */}
                        <div>
                            {/* Title and rating */}
                            <div className="mb-4">
                                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">{product.name}</h1>
                                <div className="flex items-center gap-2">
                                    <div className="flex items-center">
                                        {[...Array(5)].map((_, i) => (
                                            <Star
                                                key={i}
                                                className={cn(
                                                    'w-5 h-5',
                                                    i < Math.floor(product.average_rating || 0)
                                                        ? 'text-yellow-400 fill-current'
                                                        : 'text-gray-300'
                                                )}
                                            />
                                        ))}
                                    </div>
                                    <span className="text-sm text-gray-500">
                                        ({product.reviews_count || 0} reseñas)
                                    </span>
                                </div>
                            </div>

                            {/* Price section */}
                            <div className="mb-6">
                                {hasDiscount && (
                                    <div className="flex items-center gap-3 mb-1">
                                        <span className="bg-red-500 text-white text-xs font-medium px-2 py-1 rounded">
                                            -{discountPercentage}%
                                        </span>
                                        <span className="text-lg text-gray-500 line-through">
                                            {formatCurrency(product.compare_price || 0)}
                                        </span>
                                    </div>
                                )}
                                <div className="text-3xl font-bold text-gray-900">
                                    {formatCurrency(product.price)}
                                </div>
                                <div className="text-sm text-green-600 mt-1">
                                    {product.stock_quantity || 0 > 0 ? 'En stock' : 'Agotado'}
                                </div>
                            </div>

                            {/* Variants */}
                            {product.variants && product.variants.length > 0 && (
                                <div className="mb-6">
                                    <h3 className="text-sm font-medium text-gray-900 mb-2">
                                        {product.variants[0].name}:
                                    </h3>
                                    <div className="flex flex-wrap gap-2">
                                        {product.variants.map((variant) => (
                                            <button
                                                key={variant.id}
                                                onClick={() => setSelectedVariant(variant.id.toString())}
                                                className={cn(
                                                    'px-3 py-2 border rounded-md text-sm',
                                                    selectedVariant === variant.id.toString()
                                                        ? 'border-primary-500 bg-primary-50 text-primary-600'
                                                        : 'border-gray-300 hover:border-gray-400'
                                                )}
                                            >
                                                {variant.name}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Quantity selector */}
                            <div className="mb-8">
                                <h3 className="text-sm font-medium text-gray-900 mb-2">Cantidad:</h3>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                        disabled={quantity <= 1}
                                        className="w-10 h-10 flex items-center justify-center border border-gray-300 rounded-md bg-gray-50 disabled:opacity-50"
                                    >
                                        <Minus className="w-4 h-4" />
                                    </button>
                                    <div className="w-16 h-10 flex items-center justify-center border border-gray-300 rounded-md">
                                        {quantity}
                                    </div>
                                    <button
                                        onClick={() => setQuantity(quantity + 1)}
                                        className="w-10 h-10 flex items-center justify-center border border-gray-300 rounded-md bg-gray-50"
                                    >
                                        <Plus className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>

                            {/* Action buttons */}
                            <div className="flex flex-col sm:flex-row gap-3 mb-8">
                                <Button
                                    onClick={handleAddToCart}
                                    size="lg"
                                    className="flex-1"
                                    leftIcon={<ShoppingCart className="w-5 h-5" />}
                                >
                                    Agregar al carrito
                                </Button>
                                <Button
                                    variant="outline"
                                    size="lg"
                                    onClick={handleWishlistToggle}
                                    className={isWishlist ? 'text-red-500 border-red-300' : ''}
                                >
                                    <Heart className={`w-5 h-5 ${isWishlist ? 'fill-current text-red-500' : ''}`} />
                                </Button>
                            </div>

                            {/* Product details */}
                            <div className="border-t border-gray-200 pt-6">
                                <h3 className="text-lg font-medium text-gray-900 mb-4">Detalles del producto</h3>
                                <div className="prose prose-sm text-gray-600">
                                    {product.description || 'No hay descripción disponible.'}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Product specifications */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 mt-6 p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Especificaciones</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <p className="text-sm text-gray-500">SKU</p>
                            <p className="text-sm font-medium">{product.sku}</p>
                        </div>
                        <div className="space-y-2">
                            <p className="text-sm text-gray-500">Categoría</p>
                            <p className="text-sm font-medium">{product.category?.name || '-'}</p>
                        </div>
                        <div className="space-y-2">
                            <p className="text-sm text-gray-500">Disponibilidad</p>
                            <p className="text-sm font-medium">
                                {product.stock_quantity || 0 > 0 ? 'En stock' : 'Agotado'}
                            </p>
                        </div>
                        <div className="space-y-2">
                            <p className="text-sm text-gray-500">Vendedor</p>
                            <p className="text-sm font-medium">{product.seller?.store_name || 'Nuestro marketplace'}</p>
                        </div>
                    </div>
                </div>

                {/* Reviews section */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 mt-6 p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-6">Opiniones de clientes</h3>
                    {product.reviews && product.reviews.length > 0 ? (
                        <div className="space-y-6">
                            {product.reviews.map((review) => (
                                <div key={review.id} className="border-b border-gray-100 pb-6 last:border-0">
                                    <div className="flex items-center gap-3 mb-2">
                                        <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                                            <span className="font-medium text-gray-700">
                                                {review.user_name.charAt(0).toUpperCase()}
                                            </span>
                                        </div>
                                        <div>
                                            <p className="font-medium">{review.user_name}</p>
                                            <div className="flex items-center gap-1">
                                                {[...Array(5)].map((_, i) => (
                                                    <Star
                                                        key={i}
                                                        className={cn(
                                                            'w-4 h-4',
                                                            i < review.rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
                                                        )}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                    <p className="text-gray-600 text-sm">{review.comment}</p>
                                    <p className="text-gray-400 text-xs mt-2">
                                        {new Date(review.created_at).toLocaleDateString()}
                                    </p>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-gray-500 text-center py-4">No hay reseñas aún. Sé el primero en opinar.</p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProductDetailPage;